import { useId, type CSSProperties } from "react";

export type BrandLoaderProps = {
  /** 0-100 for uploads; omit for indeterminate loop */
  progress?: number;
  label?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
  style?: CSSProperties;
};

const SIZES = { sm: 36, md: 52, lg: 68 } as const;

/*
 * Geometry is lifted verbatim from the shield mark inside
 * public/brand/spielmann-logo.svg (viewBox 0 0 1257.46 210.4), so the loader
 * is the original logo: outlined shield, serif S, small C, dash below.
 */
const VIEW_X = 1082;
const VIEW_Y = -4;
const VIEW_W = 180;
const VIEW_H = 220;

const SHIELD_OUTLINE =
  "M1239.53,131.02c0,1.7-.26,3.34-.77,4.86-2.58,7.75-14.81,34.19-63.77,48.9-2.62.79-5.37.78-7.97-.02-17.72-5.44-49.43-18.56-62.8-45.25-1.08-2.16-1.66-4.58-1.66-7V4.94h136.87v48.88h4.94V4.94h8.37V0h-163.5v4.94h8.38v127.58c0,3.18.75,6.36,2.18,9.21,14.19,28.33,47.3,42.08,65.77,47.76,1.78.55,3.62.82,5.46.82s3.62-.26,5.38-.79c7.13-2.14,21.1-6.98,34.63-15.28,16.81-10.33,27.72-22.7,32.41-36.78.68-2.03,1.02-4.19,1.02-6.42v-27.54h-4.94v27.54Z";

const LETTER_S =
  "M1172.32,137.03c-21.8,0-30.45-11.57-30.78-26.13h-6.87v30.56h6.87v-11.42c5.68,8.34,15.17,13.5,31.1,13.5,17.89,0,34.06-10.18,34.06-30.77,0-19.37-12.27-23.96-34.89-29.6-21.58-5.38-26.54-9.6-27.68-19.69-1.53-13.43,9.04-23.56,25.25-23.89,12.74-.26,26.89,6.4,27.49,19.9v4.8h6.87v-4.77h0s0,0,0,0v-23.89h-6.87v8.42c-5.27-6.73-13.71-10.92-27.96-10.95-17.63-.04-32.3,12.47-31.65,30.09.51,14,12.39,22.24,33.29,26.49,20.93,4.26,29.99,9.93,29.99,23.65,0,14.49-12.54,23.72-28.22,23.72Z";

const LETTER_C =
  "M1253.63,88.22c-2.9,3.56-7.19,5.59-11.78,5.59-8.37,0-15.17-6.78-15.17-15.12s6.81-15.12,15.17-15.12c4.4,0,8.58,1.9,11.47,5.22l3.73-3.24c-3.82-4.4-9.36-6.92-15.2-6.92-11.09,0-20.11,9-20.11,20.05s9.02,20.05,20.11,20.05c6.08,0,11.77-2.7,15.61-7.41l-3.83-3.12Z";

/** Inner contour of the shield outline: the area the gold fill rises inside. */
const SHIELD_INTERIOR =
  "M1102.56 4.94 H1239.53 V131.02 C1239.53 132.72 1239.27 134.36 1238.76 135.88 C1236.18 143.63 1223.95 170.07 1174.99 184.78 C1172.37 185.57 1169.62 185.56 1167.02 184.76 C1149.3 179.32 1117.59 166.2 1104.22 139.51 C1103.14 137.35 1102.56 134.93 1102.56 132.52 Z";

/** Fill travel in viewBox units (interior top 4.94 to bottom point ~185.5). */
const FILL_TRAVEL = 182;

export function BrandLoader({ progress, label, size = "md", className = "", style }: BrandLoaderProps) {
  const uid = useId().replace(/:/g, "");
  const clipId = `shield-clip-${uid}`;
  const px = SIZES[size];
  const determinate = typeof progress === "number";
  const clamped = determinate ? Math.max(0, Math.min(100, progress)) : 0;
  const fillOffset = determinate ? FILL_TRAVEL * (1 - clamped / 100) : 0;
  const sizeClass = size === "sm" ? "brand-loader--sm" : "";

  return (
    <div
      className={["brand-loader", sizeClass, determinate ? "brand-loader--determinate" : "brand-loader--indeterminate", className]
        .filter(Boolean)
        .join(" ")}
      style={{ ...style, "--brand-loader-size": `${px}px` } as CSSProperties}
      role={label ? "status" : undefined}
      aria-live={label ? "polite" : undefined}
      aria-valuenow={determinate ? clamped : undefined}
      aria-valuemin={determinate ? 0 : undefined}
      aria-valuemax={determinate ? 100 : undefined}
    >
      <svg
        className="brand-loader__svg"
        viewBox={`${VIEW_X} ${VIEW_Y} ${VIEW_W} ${VIEW_H}`}
        width={px}
        height={Math.round((px * VIEW_H) / VIEW_W)}
        aria-hidden
      >
        <defs>
          <clipPath id={clipId}>
            <path d={SHIELD_INTERIOR} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          <rect
            className="brand-loader__fill"
            x={VIEW_X}
            y="4"
            width={VIEW_W}
            height={FILL_TRAVEL + 6}
            fill="var(--brand-loader-fill, #C49A2A)"
            style={determinate ? { transform: `translateY(${fillOffset}px)` } : undefined}
          />
        </g>
        <path className="brand-loader__outline" fill="currentColor" d={SHIELD_OUTLINE} />
        <path className="brand-loader__letter" fill="currentColor" d={LETTER_S} />
        <path className="brand-loader__letter" fill="currentColor" d={LETTER_C} />
        <rect className="brand-loader__dash" fill="currentColor" x="1161.61" y="205.47" width="18.67" height="4.94" />
      </svg>
      {label ? <p className="brand-loader__label">{label}</p> : null}
    </div>
  );
}
